import React, { useEffect, useMemo, useState } from 'react';
import { useDebounce } from '@kiper/hooks';
import propTypes from 'prop-types';
import { useTheme } from 'styled-components';

import StyledSelect from './styles';
import { createSemanticSelectStyles, mergeSelectStyles } from './selectStyles';

const Select = React.forwardRef(
  (
    {
      onChange,
      value,
      inputValue,
      onInputChange,
      delay,
      name: _name,
      options,
      classNamePrefix,
      clearOnSelected,
      styles: customStyles,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const debounce = useDebounce({ delay });
    const [inputData, setInputData] = useState(inputValue);
    const [selectData, setSelectData] = useState(value);

    const semanticSelectStyles = useMemo(
      () => createSemanticSelectStyles(theme.colors),
      [theme.colors],
    );

    const mergedStyles = useMemo(
      () => mergeSelectStyles(semanticSelectStyles, customStyles),
      [semanticSelectStyles, customStyles],
    );

    useEffect(() => setSelectData(value), [value]);

    useEffect(() => setInputData(inputValue), [inputValue]);

    const handleChange = data => {
      if (!clearOnSelected) setSelectData(data);
      if (onChange) onChange(data);
    };

    const handleChangeInput = data => {
      setInputData(data);
      if (onInputChange) debounce(() => onInputChange(data));
    };

    return (
      <StyledSelect
        {...props}
        options={options}
        inputValue={inputData}
        onInputChange={handleChangeInput}
        value={selectData}
        onChange={handleChange}
        classNamePrefix={classNamePrefix}
        styles={mergedStyles}
        ref={ref}
      />
    );
  },
);
export default Select;

Select.displayName = 'Select';

Select.propTypes = {
  value: propTypes.oneOfType([
    propTypes.any,
    propTypes.object,
    propTypes.array,
  ]),
  onChange: propTypes.func,
  inputValue: propTypes.string,
  onInputChange: propTypes.func,
  delay: propTypes.number,
  options: propTypes.array,
  name: propTypes.string,
  classNamePrefix: propTypes.string,
  clearOnSelected: propTypes.bool,
  styles: propTypes.object,
};

Select.defaultProps = {
  value: null,
  onChange: undefined,
  inputValue: '',
  name: '',
  classNamePrefix: 'select',
  options: [],
  onInputChange: undefined,
  delay: 0,
  clearOnSelected: false,
  styles: undefined,
};
